import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform} from "framer-motion";
import { 
  GraduationCap, ArrowRight, UserPlus, 
  MapPin, Clock, Search, LayoutDashboard, 
  CheckCircle, FileText, Activity, 
  PieChart, Star, Menu, X, Users,
  Twitter, Linkedin, Github,Quote
} from "lucide-react";

// --- Reusable Components ---
const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-neutral-900/40 backdrop-blur-xl border border-white/10 rounded-2xl ${className}`}>
    {children}
  </div>
);

// --- Sections ---

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-neutral-950/80 backdrop-blur-md border-b border-white/10 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white group">
          <GraduationCap className="h-8 w-8 text-lime-400 transition-transform group-hover:scale-110" />
          <span className="text-xl font-bold tracking-tight">Edu<span className="text-neutral-500">Portal</span></span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm font-medium text-neutral-400 hover:text-white transition-colors">Features</a>
          <a href="#how-it-works" className="text-sm font-medium text-neutral-400 hover:text-white transition-colors">How it works</a>
          {/* <a href="#testimonials" className="text-sm font-medium text-neutral-400 hover:text-white transition-colors">Testimonials</a> */}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" className="text-sm font-medium text-neutral-300 hover:text-white transition-colors">Admin Login</Link>
          <Link to="/register" className="text-sm font-medium bg-lime-400 hover:bg-lime-300 text-neutral-950 px-5 py-2.5 rounded-full transition-all flex items-center gap-2">
            Apply as Student
          </Link>
        </div>

        <button className="md:hidden text-white" onClick={() => setMobileMenu(!mobileMenu)}>
          {mobileMenu ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenu && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-neutral-900 border-b border-white/10 p-6 flex flex-col gap-4">
          <a href="#features" className="text-neutral-300">Features</a>
          <a href="#how-it-works" className="text-neutral-300">How it works</a>
          <Link to="/login" className="text-lime-400 font-medium">Admin Login</Link>
          <Link to="/register" className="bg-lime-400 text-neutral-950 px-4 py-2 rounded-full text-center font-medium">Apply as Student</Link>
        </div>
      )}
    </motion.nav>
  );
};

const HeroSection = () => {
  return (
    <section className="relative pt-40 pb-20 overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-lime-500/20 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 text-center lg:text-left">
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-800/50 border border-neutral-700/50 mb-8"
            >
              {/* <span className="flex h-2 w-2 rounded-full bg-lime-400 animate-pulse"></span> */}
              {/* <span className="text-xs font-medium text-neutral-300">Trusted by 50+ institutions</span> */}
            {/* </motion.div> */}
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white mb-6 leading-[1.1]"
            >
              The Smartest Way to <br className="hidden lg:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-emerald-400">Manage Admissions</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg md:text-xl text-neutral-400 mb-10 max-w-2xl mx-auto lg:mx-0 font-light"
            >
              Streamline student applications, approvals, and insights in one unified platform. Built for modern educational ecosystems.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
            >
              <Link to="/register" className="w-full sm:w-auto px-8 py-4 bg-lime-400 hover:bg-lime-300 text-neutral-950 rounded-full font-semibold transition-all flex items-center justify-center gap-2 group">
                Apply as Student
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" className="w-full sm:w-auto px-8 py-4 bg-neutral-800 hover:bg-neutral-700 text-white rounded-full font-semibold transition-all border border-neutral-700 flex items-center justify-center">
                Admin Login
              </Link>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex-1 w-full max-w-[600px] relative hidden md:block"
          >
            {/* Dashboard Mockup */}
            <div className="relative rounded-2xl bg-neutral-900 border border-neutral-800 shadow-2xl shadow-lime-900/20 overflow-hidden">
              {/* Header bar */}
              <div className="bg-neutral-950 p-4 border-b border-neutral-800 flex items-center gap-4">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                </div>
                <div className="flex-1 bg-neutral-900 rounded-md h-8 flex items-center px-3">
                  <Search className="h-4 w-4 text-neutral-500" />
                </div>
                <div className="w-8 h-8 rounded-full bg-lime-400/20 flex items-center justify-center">
                  <UserPlus className="h-4 w-4 text-lime-400" />
                </div>
              </div>
              {/* Content */}
              <div className="p-6 space-y-6">
                <div className="flex justify-between items-end">
                  <div>
                    <h3 className="text-white font-medium mb-1">Applications Overview</h3>
                    <p className="text-xs text-neutral-400">Real-time status of current semester</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Total</p>
                    <p className="text-2xl font-semibold text-lime-400">1,248</p>
                  </div>
                </div>
                
                {/* Chart Mock */}
                <div className="h-32 flex items-end gap-2">
                  {[40, 70, 45, 90, 65, 100, 80].map((h, i) => (
                    <div key={i} className="flex-1 bg-neutral-800 rounded-t-sm relative group">
                      <motion.div 
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                        className={`absolute bottom-0 w-full rounded-t-sm group-hover:opacity-80 transition-opacity ${i === 5 ? 'bg-lime-400' : 'bg-neutral-700'}`}
                      ></motion.div>
                    </div>
                  ))}
                </div>
                
                {/* List Mock */}
                <div className="space-y-3 pt-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-neutral-800/50">
                      <div className="w-8 h-8 rounded-full bg-neutral-700"></div>
                      <div className="flex-1">
                        <div className="h-2 w-24 bg-neutral-600 rounded mb-2"></div>
                        <div className="h-2 w-16 bg-neutral-700 rounded"></div>
                      </div>
                      <div className="h-6 w-16 bg-lime-400/10 rounded border border-lime-400/20"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute -right-8 top-16 bg-neutral-900 border border-neutral-800 rounded-xl p-4 shadow-xl flex items-center gap-3 backdrop-blur-md"
            >
              <div className="w-10 h-10 rounded-full bg-lime-400/20 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-lime-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Application Approved</p>
                <p className="text-xs text-neutral-400">Just now</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    { icon: <UserPlus />, title: "Student Registration", desc: "Easy to use profile creation framework." },
    { icon: <Activity />, title: "Application Tracking", desc: "Real-time updates on application status and next steps." },
    { icon: <LayoutDashboard />, title: "Admin Dashboard", desc: "Powerful tools for bulk approvals and user management." },
    { icon: <FileText />, title: "Document Uploads", desc: "Secure vault for academic transcripts and identity proofs." },
    { icon: <Clock />, title: "Real-time Status", desc: "Immediate notifications when decisions are made." },
    { icon: <PieChart />, title: "Analytics & Insights", desc: "Data-driven dashboards to understand admission trends." },
  ];

  return (
    <section id="features" className="py-24 relative z-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">Everything you need</h2>
          <p className="text-neutral-400 text-lg">A complete ecosystem designed to replace legacy admission processes with a fluid, modern experience.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <GlassCard className="p-8 h-full hover:bg-neutral-800/60 transition-colors group">
                <div className="w-12 h-12 rounded-lg bg-neutral-800 border border-neutral-700 flex items-center justify-center text-lime-400 mb-6 group-hover:scale-110 transition-transform">
                  {feat.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-2">{feat.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{feat.desc}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    { num: "01", title: "Sign Up", desc: "Create an account as a student or login via admin portal." },
    { num: "02", title: "Submit & Review", desc: "Students submit preferences, admins review the pool." },
    { num: "03", title: "Track & Manage", desc: "Seamless seat allocation, status updates, and reports." }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-neutral-950 border-y border-white/5 relative overflow-hidden">
      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-full max-w-lg h-full bg-neutral-900/20 blur-3xl pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">How it works</h2>
        </div>
        
        <div className="flex flex-col md:flex-row gap-8 relative">
          {/* Connecting Line */}
          <div className="hidden md:block absolute top-[44px] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-neutral-700 to-transparent"></div>
          
          {steps.map((step, idx) => (
            <div key={idx} className="flex-1 relative z-10 text-center md:text-left flex flex-col items-center md:items-start">
              <div className="w-24 h-24 rounded-full bg-neutral-900 border-2 border-neutral-800 flex items-center justify-center mb-6 shadow-xl relative group">
                <div className="absolute inset-0 rounded-full bg-lime-400/0 group-hover:bg-lime-400/10 transition-colors duration-500"></div>
                <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-white to-neutral-500">{step.num}</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">{step.title}</h3>
              <p className="text-neutral-400 max-w-xs">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DualFlowSection = () => {
  return (
    <section className="py-24 max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Student Flow */}
        <GlassCard className="p-10 relative overflow-hidden group">
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-500/10 blur-[80px] rounded-full group-hover:bg-emerald-500/20 transition-colors duration-500"></div>
          <div className="relative z-10">
            <span className="text-emerald-400 font-medium text-sm tracking-widest uppercase mb-4 block">For Students</span>
            <h2 className="text-3xl font-semibold text-white mb-6">Your Future, Simplified</h2>
            <ul className="space-y-4 mb-8">
              {["Easy signup process", "Apply to multiple programs", "Track application status live", "Document vault integration"].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-neutral-300">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/register" className="inline-flex items-center gap-2 text-white font-medium hover:text-emerald-400 transition-colors">
              Start Application <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </GlassCard>

        {/* Admin Flow */}
        <GlassCard className="p-10 relative overflow-hidden group">
          <div className="absolute -right-20 -top-20 w-64 h-64 bg-lime-500/10 blur-[80px] rounded-full group-hover:bg-lime-500/20 transition-colors duration-500"></div>
          <div className="relative z-10">
            <span className="text-lime-400 font-medium text-sm tracking-widest uppercase mb-4 block">For Admins</span>
            <h2 className="text-3xl font-semibold text-white mb-6">Complete Control</h2>
            <ul className="space-y-4 mb-8">
              {["Review incoming applications", "One-click Approve/Reject", "Manage users and roles", "Generate merit lists & reports"].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-neutral-300">
                  <LayoutDashboard className="h-5 w-5 text-lime-400" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/login" className="inline-flex items-center gap-2 text-white font-medium hover:text-lime-400 transition-colors">
              Go to Dashboard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </GlassCard>

      </div>
    </section>
  );
};

// const TestimonialsSection = () => {
//   const reviews = [
//     { quote: "The portal made applying to college completely stress-free. I knew exactly where I stood.", name: "Sarah Jenkins", role: "Incoming Freshman" },
//     { quote: "We processed 10,000+ applications without a single server crash. The dashboard is a godsend.", name: "Dr. Mark R.", role: "Head of Admissions" }
//   ];

//   return (
//     <section id="testimonials" className="py-24 max-w-7xl mx-auto px-6 border-t border-white/5">
//       <div className="text-center mb-16">
//         <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">Trusted by the best</h2>
//       </div>
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
//         {reviews.map((r, i) => (
//           <GlassCard key={i} className="p-8">
//             <Quote className="h-8 w-8 text-neutral-700 mb-6" />
//             <p className="text-xl text-neutral-300 mb-8 leading-relaxed">"{r.quote}"</p>
//             <div className="flex items-center gap-4">
//               <div className="w-12 h-12 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center">
//                 <Users className="h-5 w-5 text-neutral-500" />
//               </div>
//               <div>
//                 <p className="text-white font-medium">{r.name}</p>
//                 <p className="text-neutral-500 text-sm">{r.role}</p>
//               </div>
//             </div>
//           </GlassCard>
//         ))}
//       </div>
//     </section>
//   );
// };

const CTASection = () => (
  <section className="py-24 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-lime-500/5"></div>
    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
      <h2 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6">
        Start Your Admission <br/> Journey Today
      </h2>
      <p className="text-xl text-neutral-400 mb-10 max-w-2xl mx-auto">
        Join the platform that is reshaping the future of student enrollment.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Link to="/register" className="px-8 py-4 bg-lime-400 hover:bg-lime-300 text-neutral-950 rounded-full font-semibold transition-all">
          Student Signup
        </Link>
        <Link to="/login" className="px-8 py-4 bg-neutral-900 border border-neutral-700 hover:bg-neutral-800 text-white rounded-full font-semibold transition-all">
          Admin Signup
        </Link>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/10 bg-neutral-950 pt-16 pb-8">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-8 mb-12">
        <div>
          <Link to="/" className="flex items-center gap-2 text-white mb-4 justify-center md:justify-start">
            <GraduationCap className="h-6 w-6 text-lime-400" />
            <span className="text-lg font-bold tracking-tight">Edu<span className="text-neutral-500">Portal</span></span>
          </Link>
          <p className="text-neutral-500 text-sm max-w-xs text-center md:text-left">
            Empowering institutions to streamline their enrollment process seamlessly.
          </p>
        </div>
        <div className="flex gap-8">
          <div className="flex flex-col gap-3">
            <span className="text-white font-medium mb-1">Platform</span>
            <Link to="/features" className="text-neutral-400 hover:text-white text-sm">Features</Link>
            <Link to="/about" className="text-neutral-400 hover:text-white text-sm">About</Link>
            <Link to="/contact" className="text-neutral-400 hover:text-white text-sm">Contact</Link>
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-white font-medium mb-1">Legal</span>
            <Link to="/privacy" className="text-neutral-400 hover:text-white text-sm">Privacy</Link>
            <Link to="/terms" className="text-neutral-400 hover:text-white text-sm">Terms</Link>
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/5">
        <p className="text-neutral-500 text-sm">© 2026 EduPortal. All rights reserved.</p>
        <div className="flex items-center gap-4 text-neutral-500">
          <a href="#" className="hover:text-white transition-colors"><Twitter className="h-5 w-5" /></a>
          <a href="#" className="hover:text-white transition-colors"><Linkedin className="h-5 w-5" /></a>
          <a href="#" className="hover:text-white transition-colors"><Github className="h-5 w-5" /></a>
        </div>
      </div>
    </div>
  </footer>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-50 font-sans selection:bg-lime-400/30">
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <DualFlowSection />
      {/* <TestimonialsSection /> */}
      <CTASection />
      <Footer />
    </div>
  );
}
